import React, { useState, forwardRef } from 'react';
import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { cn } from './ui/utils';

export interface PeopleMultiSelectProps {
  values: string[];
  options: string[];
  onChange: (values: string[]) => void;
  onAddPerson: (name: string) => void;
  placeholder?: string;
  className?: string;
}

interface MultiSelectButtonProps {
  values: string[];
  placeholder?: string;
  onRemove: (value: string) => void;
  open: boolean;
  className?: string;
}

const MultiSelectButton = forwardRef<HTMLDivElement, MultiSelectButtonProps>(({ values, placeholder, onRemove, open, className, ...props }, ref) => (
  <div
    ref={ref}
    role="combobox"
    aria-expanded={open}
    className={cn(
      'flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-accent',
      className
    )}
    {...props}
  >
    <div className="flex flex-wrap gap-1 flex-1">
      {values.length === 0 ? (
        <span className="text-muted-foreground">{placeholder}</span>
      ) : (
        values.map(value => (
          <Badge
            key={value}
            variant="secondary"
            className="mr-1"
          >
            {value}
            <span
              className="ml-1 hover:bg-secondary-foreground/20 rounded-sm cursor-pointer inline-flex"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(value);
              }}
            >
              <X className="h-3 w-3" />
            </span>
          </Badge>
        ))
      )}
    </div>
    <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-2" />
  </div>
));

MultiSelectButton.displayName = 'MultiSelectButton';

export function PeopleMultiSelect({
  values,
  options,
  onChange,
  onAddPerson,
  placeholder = 'Select people...',
  className,
}: PeopleMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(search.toLowerCase())
  );

  const showCreateOption = search.trim() !== '' && 
    !options.some(opt => opt.toLowerCase() === search.toLowerCase());

  const handleToggle = (option: string) => {
    const newValues = values.includes(option)
      ? values.filter(v => v !== option)
      : [...values, option];
    onChange(newValues);
  };

  const handleCreate = () => {
    if (search.trim()) {
      const trimmedName = search.trim();
      onAddPerson(trimmedName);
      const newValues = [...values, trimmedName];
      onChange(newValues);
      setSearch('');
    }
  };

  const handleRemove = (option: string) => {
    onChange(values.filter(v => v !== option));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <MultiSelectButton
          values={values}
          placeholder={placeholder}
          onRemove={handleRemove}
          open={open}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="max-h-[300px] overflow-auto">
          {/* Search Input */}
          <div className="sticky top-0 bg-popover p-2 border-b">
            <Input
              type="text"
              placeholder="Search or add new person..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
            />
          </div>

          {/* Options List */}
          <div className="p-1">
            {/* Filtered Options */}
            {filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleToggle(option)}
                className={cn(
                  "w-full flex items-center px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer",
                  values.includes(option) && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    values.includes(option) ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {option}
              </button>
            ))}

            {/* Create Option */}
            {showCreateOption && (
              <button
                type="button"
                onClick={handleCreate}
                className="w-full flex items-center px-2 py-1.5 text-sm rounded hover:bg-accent cursor-pointer text-primary border-t mt-1 pt-2"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add "{search}"
              </button>
            )}

            {/* Empty State */}
            {filteredOptions.length === 0 && !showCreateOption && (
              <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                No people found.
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
